import { Content, List, Root as Tabs, Trigger } from '@radix-ui/react-tabs';
import classNames from 'classnames';
import { Spinner } from 'components/spinner';
import { IppicaTicket } from 'features/ippica/components/ippicaTicket';
import { SportTicket } from 'features/sport/components/sportTicket';
import { VirtualTicket } from 'features/virtual/components/virtualTicket';
import { useInternalSession } from 'hooks';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { MovimentiTipoScommessaEnum } from 'types/swagger';
import { isClientSide, isGermanSite } from 'utility/constant';
import { CarrelloContentProps } from '.';
import { CarrelloScommesse } from '../carrelloScommesse/CarrelloScommesse';
import {
  selectAvvenimentiByTicket,
  selectIsFullCart,
  selectIsLoadingVendita,
  selectNumEsitiEveryTicket,
  selectOpenedBet,
  selectStato,
  selectTab,
  selectTipo,
} from '../carrelloSelectors';
import { tabSelected } from '../carrelloSlice';
import { useHasMultipleCashes } from 'features/carrello/hooks';
import { CarrelloTabEnum } from '../types';
import styles from './CarrelloContent.module.scss';
import { CarrelloContentDropdown } from './carrelloContentDropdown';
import { CarrelloContentEmpty } from './carrelloContentEmpty';
import { PrenotazioniContent } from './prenotazioniContent';

interface LocalStorageEntry {
  key: string;
  value: any;
}

export const CarrelloContent = memo(function CarrelloContent({
  labelBiglietto,
  labelScommesse,
  isActive,
  type,
}: CarrelloContentProps) {
  const dispatch = useAppDispatch();
  const [localStorageData, setLocalStorageData] = useState<string[]>([]);

  const { isAuthenticated } = useInternalSession();

  const openedBet = useTypedSelector(selectOpenedBet);
  const isFullCart = useTypedSelector(selectIsFullCart);
  const avvenimenti = useTypedSelector((state) => selectAvvenimentiByTicket(state, type));
  const isLoadingVendita = useTypedSelector(selectIsLoadingVendita);
  const hasMultipleCashes = useHasMultipleCashes();
  const { numEsitiEveryTicket, isActive: isBigliettoActive } = useTypedSelector(selectNumEsitiEveryTicket);

  const tab = useTypedSelector(selectTab);
  const tipo = useTypedSelector(selectTipo);
  const stato = useTypedSelector(selectStato);

  const traceRef = useRef(false);
  useEffect(() => {
    if (isClientSide) {
      const search = new URLSearchParams(document.location.search.substring(1));
      const trace = search.get('trace') ?? '';
      traceRef.current = trace.toLowerCase().includes('bets');
    }
  }, []);

  const objTickets = () => {
    if (traceRef.current) {
      console.log(`MyBets: CarrelloContent.selezione tab:${tab}, tipo:${tipo}, stato:${stato}`);
    }
    const key = `list-${tab}-${tipo}-${stato}`;
    switch (tipo) {
      case MovimentiTipoScommessaEnum.Virtual:
        return <VirtualTicket key={key} />;
      case MovimentiTipoScommessaEnum.Ippica:
        return <IppicaTicket key={key} />;
      case MovimentiTipoScommessaEnum.Sport:
      default:
        return <SportTicket key={key} />;
    }
  };

  const labelNessunaGiocata = useGetLabelByKey('nessuna-giocata');
  const labelSelezionaQuota = useGetLabelByKey('seleziona-una-quota-per-iniziare');
  const labelScommessaInAccettazione = useGetLabelByKey('scommessa-in-accettazione-attendi');

  useEffect(() => {
    const prenotazioniLocalStorageElements = ['prenotazione-scommessa-codicesessione-sport'];

    // Initialize an empty array to store the retrieved data
    const retrievedData: LocalStorageEntry[] = [];

    // Loop through the keys and read data from local storage
    prenotazioniLocalStorageElements.forEach((key) => {
      const item = localStorage.getItem(key);
      if (item) {
        retrievedData.push({ key, value: JSON.parse(item) });
      }
    });
    retrievedData && retrievedData.length > 0 && setLocalStorageData(retrievedData[0].value);
  }, []);

  const handleLocalStorageChange = useCallback(() => {
    const prenotazioniLocalStorageElements = ['prenotazione-scommessa-codicesessione-sport'];

    // Initialize an empty array to store the retrieved data
    const retrievedData: LocalStorageEntry[] = [];

    // Loop through the keys and read data from local storage
    prenotazioniLocalStorageElements.forEach((key) => {
      const item = localStorage.getItem(key);
      if (item) {
        retrievedData.push({ key, value: JSON.parse(item) });
      }
    });

    retrievedData && retrievedData.length > 0 && setLocalStorageData(retrievedData[0].value);
  }, []);

  useEffect(() => {
    if (isClientSide) {
      // Add an event listener to detect changes in localStorage.
      window.addEventListener('storage', handleLocalStorageChange);
    }

    return () => {
      if (isClientSide) {
        window.removeEventListener('storage', handleLocalStorageChange);
      }
    };
  }, [handleLocalStorageChange]);

  return (
    <div className={classNames(styles.container, { [styles.mini]: !isFullCart, [styles.hide]: !isActive })}>
      {isFullCart ? (
        <Tabs
          value={tab}
          className={styles.root}
          defaultValue={tab}
          onValueChange={(value) => dispatch(tabSelected(value as CarrelloTabEnum))}
        >
          <List>
            {/** Biglietto Tab */}
            <Trigger value={CarrelloTabEnum.Biglietto} className={styles.tab}>
              {labelBiglietto}
              {isBigliettoActive && <span className={styles.number}>{numEsitiEveryTicket}</span>}
            </Trigger>
            {/** Scommesse Tab */}
            <Trigger value={CarrelloTabEnum.Scommesse} className={styles.tab}>
              {labelScommesse}
              {isAuthenticated
                ? openedBet && <span className={styles.number}>{openedBet}</span>
                : localStorageData &&
                  localStorageData.length > 0 && <span className={styles.number}>{localStorageData.length}</span>}
            </Trigger>
          </List>
          {/** Biglietto Content */}
          <Content value={CarrelloTabEnum.Biglietto} className={styles.content}>
            {hasMultipleCashes && !isGermanSite && <CarrelloContentDropdown showCounter />}
            {!avvenimenti || avvenimenti?.length === 0 ? (
              <CarrelloContentEmpty title={labelNessunaGiocata!} description={labelSelezionaQuota} />
            ) : (
              objTickets()
            )}
            {isLoadingVendita && (
              <div className={styles.loadingContainer}>
                <Spinner className={styles.loading} />
                <p className={styles.title}>{labelScommessaInAccettazione}</p>
              </div>
            )}
          </Content>
          {/** Scommesse Content */}
          <Content value={CarrelloTabEnum.Scommesse} className={styles.content}>
            {hasMultipleCashes && !isGermanSite && <CarrelloContentDropdown />}
            {isAuthenticated ? (
              <CarrelloScommesse />
            ) : (
              <PrenotazioniContent refreshLocalStorage={setLocalStorageData} />
            )}
          </Content>
        </Tabs>
      ) : (
        objTickets()
      )}
    </div>
  );
});
